import {EventEmitter} from "node:events";
import type {SuperJSON} from "superjson";
import type {AppendOnlyLog} from "./log.js";
import {MessageProcessor} from "./processor.js";
import type {FsDuplexMessage, FsDuplexMessageType, FsDuplexParty, FsDuplexPayloads} from "./protocol.js";

export type MessageChannelEventMap = {
  [key in FsDuplexMessageType]: [FsDuplexPayloads[key]];
} & {
  open: [];
  close: [];
  error: [Error];
};

export class MessageChannel<P extends FsDuplexParty> extends EventEmitter<MessageChannelEventMap> {
  public readonly party: P;
  public readonly log: AppendOnlyLog;
  private superjson: SuperJSON;
  private processor: MessageProcessor;
  private state: "opening" | "open" | "closing" | "closed" = "opening";
  private localSeq = 0;
  private localAck = 0;

  public get currentState() {
    return this.state;
  }

  constructor(party: P, superjson: SuperJSON, log: AppendOnlyLog) {
    super();
    this.party = party;
    this.superjson = superjson;
    this.log = log;
    this.processor = new MessageProcessor(party);
  }

  public async handleLogData(): Promise<void> {
    const lines = await this.log.readNewLines();
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const message = this.superjson.parse<FsDuplexMessage>(line);
        const result = this.processor.process(message, this.state, this.localAck);

        const actions = result.actions;
        for (const action of actions) {
          if (action.type === "emit") {
            if (action.event === "open" || action.event === "close") {
              this.emit(action.event);
            } else {
              this.emit(action.event, action.payload);
            }
          } else if (action.type === "send") {
            this.send(action.messageType, action.payload);
          }
        }
        this.state = result.newState;
        this.localAck = result.newLocalAck;
      } catch (error) {
        this.emit("error", new Error(`Failed to parse message line: "${line}"`));
      }
    }
  }

  public send<T extends FsDuplexMessageType>(type: T, payload: FsDuplexPayloads[T]): void {
    if (this.state === "closed") return;
    if (this.state === "closing" && type !== "close" && type !== "close_ack") return;
    this.localSeq++;
    const message: FsDuplexMessage = {from: this.party, seq: this.localSeq, ack: this.localAck, type, payload};
    const line = this.superjson.stringify(message);
    this.log.append(line).catch((err) => this.emit("error", err));
  }

  public close(): void {
    if (this.state === "closing" || this.state === "closed") return;
    this.state = "closing";
    this.send("close", {reason: "done"});
  }
}
